import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, Play, Clock, Eye, Headphones, Youtube, ExternalLink, Instagram, Facebook } from 'lucide-react';

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KathaPage = () => {
  const [kathaList, setKathaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedKatha, setSelectedKatha] = useState(null);

  const categories = ['All', 'Nitnem', 'Banis', 'History', 'Philosophy', 'Gurpurab'];

  const openYouTubeSearch = () => {
    const query = searchTerm || 'Sikh Katha Gurbani';
    window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`, '_blank');
  };

  const openInstagramSearch = () => {
    const query = searchTerm || 'Sikh Katha';
    window.open(`https://www.instagram.com/explore/tags/${encodeURIComponent(query.replace(/\s+/g, ''))}`, '_blank');
  };

  const openFacebookSearch = () => {
    const query = searchTerm || 'Sikh Katha Gurbani';
    window.open(`https://www.facebook.com/search/videos?q=${encodeURIComponent(query)}`, '_blank');
  };

  const openTikTokSearch = () => {
    const query = searchTerm || 'Sikh Katha';
    window.open(`https://www.tiktok.com/search?q=${encodeURIComponent(query)}`, '_blank');
  };

  useEffect(() => {
    const fetchKatha = async () => {
      try {
        const response = await axios.get(`${API_URL}/katha`);
        setKathaList(response.data);
      } catch (error) {
        console.error('Error fetching katha:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchKatha();
  }, []);

  const filteredKatha = kathaList.filter(katha => {
    const matchesSearch = katha.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      katha.speaker.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === 'All' || katha.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="katha-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Katha Library
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore spiritual discourses on Gurbani, Sikh history, and philosophy from renowned speakers.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search katha by title or speaker..."
                className="pl-12 py-6 rounded-full border-saffron-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="katha-search"
              />
            </div>
            {/* Social Media Search Buttons */}
            <div className="flex gap-2 justify-center sm:justify-start">
              <Button
                onClick={openYouTubeSearch}
                className="bg-red-600 hover:bg-red-700 text-white rounded-full p-3 sm:px-4 sm:py-6"
                title="Search on YouTube"
                data-testid="youtube-search-btn"
              >
                <Youtube className="w-5 h-5" />
                <span className="hidden lg:inline ml-2">YouTube</span>
              </Button>
              <Button
                onClick={openInstagramSearch}
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white rounded-full p-3 sm:px-4 sm:py-6"
                title="Search on Instagram"
                data-testid="instagram-search-btn"
              >
                <Instagram className="w-5 h-5" />
                <span className="hidden lg:inline ml-2">Instagram</span>
              </Button>
              <Button
                onClick={openFacebookSearch}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 sm:px-4 sm:py-6"
                title="Search on Facebook"
                data-testid="facebook-search-btn"
              >
                <Facebook className="w-5 h-5" />
                <span className="hidden lg:inline ml-2">Facebook</span>
              </Button>
              <Button
                onClick={openTikTokSearch}
                className="bg-black hover:bg-gray-800 text-white rounded-full p-3 sm:px-4 sm:py-6"
                title="Search on TikTok"
                data-testid="tiktok-search-btn"
              >
                <TikTokIcon className="w-5 h-5" />
                <span className="hidden lg:inline ml-2">TikTok</span>
              </Button>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category || (category === 'All' && !selectedCategory) ? 'default' : 'outline'}
                className={`rounded-full ${selectedCategory === category || (category === 'All' && !selectedCategory) ? 'bg-saffron-500 hover:bg-saffron-600' : 'border-saffron-200 text-saffron-700 hover:bg-saffron-50'}`}
                onClick={() => setSelectedCategory(category === 'All' ? '' : category)}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Video Player Modal */}
        {selectedKatha && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedKatha(null)}>
            <div className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-video">
                {extractYouTubeId(selectedKatha.youtube_url) ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(selectedKatha.youtube_url)}?autoplay=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <Headphones className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl font-semibold mb-2">{selectedKatha.title}</h3>
                <p className="text-gray-600 mb-1">{selectedKatha.speaker}</p>
                <p className="text-gray-500 text-sm mb-4">{selectedKatha.description}</p>
                <div className="flex items-center gap-3">
                  <Button onClick={() => setSelectedKatha(null)} className="btn-outline">
                    Close
                  </Button>
                  {selectedKatha.youtube_url && (
                    <Button 
                      onClick={() => window.open(selectedKatha.youtube_url, '_blank')}
                      className="bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2"
                    >
                      <Youtube className="w-4 h-4" />
                      Watch on YouTube
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Katha Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-gray-200"></div>
                <CardContent className="p-4">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredKatha.length === 0 ? (
          <div className="text-center py-12">
            <Headphones className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600">No katha found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
            <Button
              onClick={openYouTubeSearch}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 py-2 inline-flex items-center gap-2"
            >
              <Youtube className="w-5 h-5" />
              Search &ldquo;{searchTerm || 'Sikh Katha'}&rdquo; on YouTube
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredKatha.map((katha, index) => (
              <Card 
                key={katha.id} 
                className="card-hover bg-white border-gray-100 overflow-hidden cursor-pointer"
                onClick={() => setSelectedKatha(katha)}
                data-testid={`katha-card-${katha.id}`}
              >
                <div className="aspect-video bg-royal-900 relative group">
                  {extractYouTubeId(katha.youtube_url) ? (
                    <img
                      src={`https://img.youtube.com/vi/${extractYouTubeId(katha.youtube_url)}/maxresdefault.jpg`}
                      alt={katha.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Headphones className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 bg-saffron-500 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                  <Badge className="absolute top-3 left-3 bg-royal-600 text-white">
                    {katha.category}
                  </Badge>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                    {katha.title}
                  </h3>
                  <p className="text-saffron-600 text-sm font-medium mb-2">{katha.speaker}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {katha.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {katha.views?.toLocaleString() || 0}
                      </span>
                    </div>
                    {katha.youtube_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(katha.youtube_url, '_blank');
                        }}
                        className="text-red-600 hover:text-red-700 flex items-center gap-1 text-xs font-medium"
                        title="Watch on YouTube"
                      >
                        <Youtube className="w-4 h-4" />
                        <span className="hidden sm:inline">YouTube</span>
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KathaPage;
